import { inject, Service } from '@angular/core';
import { TranslateCompiler, TranslateParser } from '@ngx-translate/core';
import type {
  InterpolatableTranslation,
  InterpolatableTranslationObject,
  InterpolationParameters,
  StrictTranslation,
  TranslationObject,
} from '@ngx-translate/core';
import { ApiEnums, ApiTypes } from '@app/api';
import { LocalStorageService } from '@core/providers/storage/local-storage.service';
import { CONSTANTS } from '@shared/constants';
import { toChildrenType } from '@shared/utils/to-children-type';

const UNIT_TYPE_VARIANTS: Record<ApiTypes.ChildrenTypeName, string> = {
  [ApiEnums.ChildrenTypeName.Venue]: 'FOR_VENUE',
  [ApiEnums.ChildrenTypeName.Room]: 'FOR_ROOM',
  [ApiEnums.ChildrenTypeName.Product]: 'FOR_PRODUCT',
};

export const UNIT_TYPE_VARIANT_KEYS = Object.values(UNIT_TYPE_VARIANTS);

/**
 * Copy written once per unit type, as `{ "FOR_VENUE": …, "FOR_ROOM": …, "FOR_PRODUCT": … }`, reads
 * as the current account's variant under its own key. The unit word's gender changes the article
 * and the endings around it in most locales, so no single sentence fits all three.
 */
@Service({ autoProvided: false })
export class UnitTypeTranslateCompiler extends TranslateCompiler {
  private localStorage = inject(LocalStorageService);
  private parser = inject(TranslateParser);

  compile(value: string): InterpolatableTranslation {
    return value;
  }

  compileTranslations(translations: TranslationObject): InterpolatableTranslationObject {
    return this.compileObject(translations);
  }

  private compileObject(translations: TranslationObject): InterpolatableTranslationObject {
    const compiled: InterpolatableTranslationObject = {};

    for (const [key, value] of Object.entries(translations)) {
      compiled[key] = this.compileValue(value);
    }

    return compiled;
  }

  private compileValue(value: StrictTranslation): InterpolatableTranslation {
    if (isUnitTypeVariants(value)) {
      return (params?: InterpolationParameters) =>
        this.parser.interpolate(value[this.currentVariant()], params) ?? '';
    }

    return isTranslationObject(value) ? this.compileObject(value) : value;
  }

  private currentVariant(): string {
    return UNIT_TYPE_VARIANTS[
      toChildrenType(this.localStorage.retrieve(CONSTANTS.LOCAL_STORAGE_KEYS.CHILDREN_CONTEXT))
    ];
  }
}

function isTranslationObject(value: unknown): value is TranslationObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isUnitTypeVariants(value: unknown): value is Record<string, string> {
  if (!isTranslationObject(value)) {
    return false;
  }

  const keys = Object.keys(value);

  return (
    keys.length === UNIT_TYPE_VARIANT_KEYS.length &&
    UNIT_TYPE_VARIANT_KEYS.every(key => typeof value[key] === 'string')
  );
}
